import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { apiGet, apiPost, apiPut, ApiError } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

/**
 * The survey builder (docs/survey-object.md). Edits a DRAFT version's questions,
 * answers and branching, then publishes it (freezing it). A published version is
 * read-only here; "Open a new draft" clones it forward for further edits, codes
 * preserved so saved universes keep meaning across the change.
 *
 * The campaign for the write body comes from the selected-campaign context. The
 * stored functions re-check the version's true owning campaign regardless, so
 * that is the security backstop, not this.
 */

const KINDS = ['single', 'multi', 'scale', 'number', 'text'] as const
type Kind = (typeof KINDS)[number]
const hasAnswers = (kind: Kind) => kind === 'single' || kind === 'multi'

interface AnswerDraft {
  code: string
  label: string
  support_level: number | null
  next_question_code: string
}
interface QuestionDraft {
  code: string
  prompt: string
  kind: Kind
  required: boolean
  answers: AnswerDraft[]
}

interface Definition {
  survey_id: string
  version: number
  status: 'draft' | 'published'
  title: string
  questions: {
    code: string
    prompt: string
    kind: Kind
    required: boolean
    answers: { code: string; label: string; support_level: number | null }[]
    branches: { answer_code: string; next_question_code: string }[]
  }[]
}

function toDraft(def: Definition): QuestionDraft[] {
  return def.questions.map((q) => ({
    code: q.code,
    prompt: q.prompt,
    kind: q.kind,
    required: q.required,
    answers: q.answers.map((a) => ({
      code: a.code,
      label: a.label,
      support_level: a.support_level,
      next_question_code:
        q.branches.find((b) => b.answer_code === a.code)?.next_question_code ?? '',
    })),
  }))
}

/** The spec shape save_survey_draft accepts. Ordinals are assigned by position. */
function toSpec(questions: QuestionDraft[]) {
  return {
    questions: questions.map((q, qi) => ({
      code: q.code,
      prompt: q.prompt,
      kind: q.kind,
      required: q.required,
      ordinal: qi + 1,
      page: 1,
      answers: hasAnswers(q.kind)
        ? q.answers.map((a, ai) => ({
            code: a.code,
            label: a.label,
            ordinal: ai + 1,
            support_level: a.support_level,
          }))
        : [],
      branches: hasAnswers(q.kind)
        ? q.answers
            .filter((a) => a.next_question_code)
            .map((a) => ({ answer_code: a.code, next_question_code: a.next_question_code }))
        : [],
    })),
  }
}

export function SurveyBuilder() {
  const { versionId } = useParams({ from: '/surveys/$versionId' })
  const { campaignId } = useSelectedCampaign()
  const navigate = useNavigate()

  const [def, setDef] = useState<Definition | null>(null)
  const [questions, setQuestions] = useState<QuestionDraft[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    const { definition } = await apiGet<{ definition: Definition }>(
      `/surveys/definition/${versionId}`,
    )
    setDef(definition)
    setQuestions(toDraft(definition))
  }

  useEffect(() => {
    void load().catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load the survey'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId])

  const readOnly = def?.status === 'published'

  function updateQuestion(i: number, patch: Partial<QuestionDraft>) {
    setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)))
  }
  function updateAnswer(qi: number, ai: number, patch: Partial<AnswerDraft>) {
    setQuestions((qs) =>
      qs.map((q, j) =>
        j === qi ? { ...q, answers: q.answers.map((a, k) => (k === ai ? { ...a, ...patch } : a)) } : q,
      ),
    )
  }
  const addQuestion = () =>
    setQuestions((qs) => [
      ...qs,
      { code: `q${qs.length + 1}`, prompt: '', kind: 'single', required: false, answers: [] },
    ])
  const removeQuestion = (i: number) => setQuestions((qs) => qs.filter((_, j) => j !== i))
  const addAnswer = (qi: number) =>
    updateQuestion(qi, {
      answers: [
        ...questions[qi]!.answers,
        { code: '', label: '', support_level: null, next_question_code: '' },
      ],
    })
  const removeAnswer = (qi: number, ai: number) =>
    updateQuestion(qi, { answers: questions[qi]!.answers.filter((_, k) => k !== ai) })

  async function save() {
    if (!campaignId) return
    setBusy(true)
    setError(null)
    try {
      await apiPut(`/surveys/draft/${versionId}`, { campaign_id: campaignId, spec: toSpec(questions) })
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  async function publish() {
    if (!campaignId || !confirm('Publish this survey? A published version cannot be edited.')) return
    setBusy(true)
    setError(null)
    try {
      await apiPut(`/surveys/draft/${versionId}`, { campaign_id: campaignId, spec: toSpec(questions) })
      await apiPost(`/surveys/publish/${versionId}`, { campaign_id: campaignId })
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Publish failed')
    } finally {
      setBusy(false)
    }
  }

  async function openDraft() {
    if (!campaignId || !def) return
    try {
      const { survey_version_id } = await apiPost<{ survey_version_id: string }>(
        `/surveys/draft/new/${def.survey_id}`,
        { campaign_id: campaignId },
      )
      void navigate({ to: '/surveys/$versionId', params: { versionId: survey_version_id } })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open a new draft')
    }
  }

  if (!def) {
    return <p className="text-sm text-ink-muted">{error ?? 'Loading…'}</p>
  }

  const otherQuestions = (self: string) => questions.filter((q) => q.code && q.code !== self)

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{def.title}</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Version {def.version} · {readOnly ? 'published (read-only)' : 'draft'}
          </p>
        </div>
        <div className="flex gap-2">
          {readOnly ? (
            <button
              type="button"
              onClick={() => void openDraft()}
              className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
            >
              Open a new draft
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void save()}
                className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-ink hover:border-brand-400 disabled:opacity-50"
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={busy || questions.length === 0}
                onClick={() => void publish()}
                className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
              >
                Publish
              </button>
            </>
          )}
        </div>
      </div>

      {error ? <p className="mt-4 text-sm text-ink">{error}</p> : null}

      <div className="mt-6 space-y-6">
        {questions.map((q, qi) => (
          <fieldset key={qi} className="rounded-lg border border-line p-4">
            <div className="flex flex-wrap items-center gap-3">
              <input
                aria-label="Question code"
                value={q.code}
                disabled={readOnly}
                onChange={(e) => updateQuestion(qi, { code: e.target.value })}
                placeholder="code"
                className="w-32 rounded-md border border-line px-2 py-1 text-sm"
              />
              <select
                aria-label="Question kind"
                value={q.kind}
                disabled={readOnly}
                onChange={(e) => updateQuestion(qi, { kind: e.target.value as Kind })}
                className="rounded-md border border-line px-2 py-1 text-sm"
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-sm text-ink-muted">
                <input
                  type="checkbox"
                  checked={q.required}
                  disabled={readOnly}
                  onChange={(e) => updateQuestion(qi, { required: e.target.checked })}
                />
                required
              </label>
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => removeQuestion(qi)}
                  className="ml-auto text-sm text-ink-muted hover:text-ink hover:underline"
                >
                  Remove
                </button>
              ) : null}
            </div>
            <input
              aria-label="Prompt"
              value={q.prompt}
              disabled={readOnly}
              onChange={(e) => updateQuestion(qi, { prompt: e.target.value })}
              placeholder="Question prompt"
              className="mt-3 w-full rounded-md border border-line px-2 py-1 text-sm"
            />

            {hasAnswers(q.kind) ? (
              <div className="mt-4 space-y-2">
                {q.answers.map((a, ai) => (
                  <div key={ai} className="flex flex-wrap items-center gap-2">
                    <input
                      aria-label="Answer code"
                      value={a.code}
                      disabled={readOnly}
                      onChange={(e) => updateAnswer(qi, ai, { code: e.target.value })}
                      placeholder="code"
                      className="w-24 rounded-md border border-line px-2 py-1 text-sm"
                    />
                    <input
                      aria-label="Answer label"
                      value={a.label}
                      disabled={readOnly}
                      onChange={(e) => updateAnswer(qi, ai, { label: e.target.value })}
                      placeholder="label"
                      className="flex-1 rounded-md border border-line px-2 py-1 text-sm"
                    />
                    <select
                      aria-label="Support level"
                      value={a.support_level ?? ''}
                      disabled={readOnly}
                      onChange={(e) =>
                        updateAnswer(qi, ai, {
                          support_level: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className="rounded-md border border-line px-2 py-1 text-sm"
                      title="Support level"
                    >
                      <option value="">support —</option>
                      <option value="2">+2 strong yes</option>
                      <option value="1">+1 lean yes</option>
                      <option value="0">0 undecided</option>
                      <option value="-1">−1 lean no</option>
                      <option value="-2">−2 strong no</option>
                    </select>
                    <select
                      aria-label="Then go to"
                      value={a.next_question_code}
                      disabled={readOnly}
                      onChange={(e) => updateAnswer(qi, ai, { next_question_code: e.target.value })}
                      className="rounded-md border border-line px-2 py-1 text-sm"
                      title="Branch to"
                    >
                      <option value="">next in order</option>
                      {otherQuestions(q.code).map((oq) => (
                        <option key={oq.code} value={oq.code}>
                          → {oq.code}
                        </option>
                      ))}
                    </select>
                    {!readOnly ? (
                      <button
                        type="button"
                        onClick={() => removeAnswer(qi, ai)}
                        className="text-sm text-ink-muted hover:text-ink hover:underline"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                ))}
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => addAnswer(qi)}
                    className="text-sm text-brand-700 hover:underline"
                  >
                    + Add answer
                  </button>
                ) : null}
              </div>
            ) : null}
          </fieldset>
        ))}

        {!readOnly ? (
          <button
            type="button"
            onClick={addQuestion}
            className="rounded-md border border-dashed border-line px-3 py-2 text-sm font-medium text-ink-muted hover:border-brand-400 hover:text-ink"
          >
            + Add question
          </button>
        ) : null}
      </div>
    </div>
  )
}
